export interface LeaderboardPlayer { userId: number; name: string; avatarUrl: string | null }
export interface LeaderboardStanding { rank: number; players: LeaderboardPlayer[]; value: number }
export interface SquadLeaderboard {
  squadId: number; squadName: string; month: string; timezone: string; currentMonth: string; updatedAt: string;
  played: LeaderboardStanding[]; wins: LeaderboardStanding[]; partnerships: LeaderboardStanding[];
  personal: { name: string; played: number; wins: number; playingDays: number; previousPlayingDays: number; visible: boolean };
  ratingsState: 'NOT_PUBLISHED';
  standingsLimit?:StandingsLimit;
  period?: 'MONTH' | 'LAST_3_MONTHS' | 'ALL_TIME';
  firstMonth?: string | null;
  monthlySummary?: { month: string; played: number; wins: number; playingDays: number }[];
}
export type StandingsLimit = '5'|'10'|'ALL';
export type LeaderboardMetric = 'played' | 'wins' | 'partnerships' | 'ratings';
export function previousMonth(month: string): string {
  const [year, value] = month.split('-').map(Number);
  return `${value === 1 ? year - 1 : year}-${String(value === 1 ? 12 : value - 1).padStart(2, '0')}`;
}
export function monthLabel(month: string): string {
  return new Intl.DateTimeFormat(undefined, { month: 'long', year: 'numeric', timeZone: 'UTC' })
    .format(new Date(`${month}-01T12:00:00Z`));
}
export const leaderboardRules = 'Only shared, scored results from this squad count, using saved players, not RSVPs. Each rotating set counts as one match; a fixed-lineup match counts once. Imported results follow the same rules. Practice counts for Played and Partnerships, but not Wins. Playing days are distinct dates in the squad timezone. Only current members who choose to appear are listed; guests are never matched by name. The leading five places include ties. Your summary is visible only to you.';

export function isRangePeriod(period?: string): boolean {
  return period === 'LAST_3_MONTHS' || period === 'ALL_TIME';
}
export function periodLabel(board: SquadLeaderboard): string {
  if (!isRangePeriod(board.period)) return monthLabel(board.month);
  const through = new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric', year: 'numeric', timeZone: board.timezone })
    .format(new Date(board.updatedAt));
  const start = board.period === 'LAST_3_MONTHS' ? previousMonth(previousMonth(board.currentMonth)) : board.firstMonth;
  return `${board.period === 'ALL_TIME' ? 'All time' : 'Last 3 months'}${start ? ` · ${monthLabel(start)}` : ''} – ${through}`;
}
export const periodRules = 'Last 3 months includes this calendar month and the two preceding months, through today. All time includes eligible imported history, even from before this squad joined TARK. Monthly summaries are private to you; tap a month to open its squad standings.';

export type PairResultFilter = 'ALL'|'WIN'|'LOSS'|'DRAW';
export interface PairGame {
  key:string;matchId:string;ordinal:number;playedAt:string;timezone:string;timePrecision:string;
  result:Exclude<PairResultFilter,'ALL'>;reason:string|null;practice:boolean;players:string[];opponents:string[];
  sets:NonNullable<import('./index').MatchScorecard['sets']>;formatLabel:string;
}
export interface PairHistory {
  squadId:number;squadName:string;players:LeaderboardPlayer[];month:string;currentMonth:string;
  period:'MONTH'|'LAST_3_MONTHS'|'ALL_TIME';timezone:string;total:number;wins:number;losses:number;draws:number;
  matches:PairGame[];hasMore:boolean;nextCursor:string|null;
}
export function pairResultReason(game:PairGame):string|null {
  if(game.practice) return 'Practice · no competitive result';
  if(game.result!=='DRAW') return null;
  if(game.reason==='ABANDONED'||game.reason==='RETIRED'||game.reason==='INCOMPLETE') return 'Stopped before a result';
  if(game.reason==='AGREED_DRAW'||game.reason==='DRAW'||game.reason==='TIMED') return 'Agreed tie';
  return 'No winner recorded';
}
